// src/api/searchApi.ts

import { baseApi } from './baseApi';

// ============================================================
// Types
// ============================================================

export type SearchResultType =
  | 'contact'
  | 'task'
  | 'opportunity'
  | 'pipeline'
  | 'event'
  | 'file'
  | 'form'
  | 'project'
  | 'message'
  | 'channel'
  | 'team_member'
  | 'workspace';

export interface SearchResult {
  id: string;
  type: SearchResultType;
  title: string;
  subtitle?: string;
  route: string;
  workspaceId: string;
}

export interface GlobalSearchParams {
  query: string;
  types?: SearchResultType[];
  limit?: number;
}

export interface GlobalSearchResponse {
  results: SearchResult[];
  total: number;
}

export interface SearchTypesResponse {
  types: SearchResultType[];
}

// ============================================================
// Search Index Types (Unified Search)
// ============================================================

export type SearchEntityType =
  | 'contact'
  | 'thread'
  | 'message'
  | 'task'
  | 'opportunity'
  | 'project'
  | 'file'
  | 'event'
  | 'activity'
  | 'notification'
  | 'voicemail'
  | 'call'
  | 'channel'
  | 'portfolio'
  | 'form'
  | 'memory';

export interface SearchIndexResult {
  id: string;
  entityType: SearchEntityType;
  entityId: string;
  primaryText: string;
  secondaryText: string | null;
  metadata: Record<string, unknown> | null;
  score: number;
  updatedAt: string;
}

export interface SearchIndexParams {
  q: string;
  types?: SearchEntityType[];
  limit?: number;
  offset?: number;
  sortBy?: 'relevance' | 'recency' | 'boost';
}

export interface SearchIndexResponse {
  query: string;
  workspaceId: string;
  results: SearchIndexResult[];
  total: number;
  limit: number;
  offset: number;
}

export interface SearchIndexStatsResponse {
  workspaceId: string;
  totalEntries: number;
  byEntityType: Record<string, number>;
}

export interface SearchSuggestion {
  entityType: SearchEntityType;
  entityId: string;
  text: string;
  subtext: string | null;
  score: number;
}

export interface SearchSuggestionsResponse {
  suggestions: SearchSuggestion[];
}

// ============================================================
// API Slice
// ============================================================

export const searchApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    globalSearch: builder.query<GlobalSearchResponse, GlobalSearchParams>({
      query: ({ query, types, limit }) => {
        const params = new URLSearchParams();
        params.append('query', query);
        if (types && types.length > 0) {
          params.append('types', types.join(','));
        }
        if (limit) {
          params.append('limit', limit.toString());
        }
        return `/search/global?${params.toString()}`;
      },
      providesTags: ['GlobalSearch'],
    }),

    getSearchTypes: builder.query<SearchTypesResponse, void>({
      query: () => '/search/types',
    }),

    // ============================================================
    // Unified Search Index Endpoints
    // ============================================================

    /**
     * Full-text search across all indexed entities
     * Uses PostgreSQL tsvector for fast, relevance-ranked results
     */
    searchIndex: builder.query<SearchIndexResponse, SearchIndexParams>({
      query: ({ q, types, limit, offset, sortBy }) => {
        const params = new URLSearchParams();
        params.append('q', q);
        if (types && types.length > 0) {
          params.append('types', types.join(','));
        }
        if (limit !== undefined) {
          params.append('limit', limit.toString());
        }
        if (offset !== undefined) {
          params.append('offset', offset.toString());
        }
        if (sortBy) {
          params.append('sortBy', sortBy);
        }
        return `/search/index?${params.toString()}`;
      },
      providesTags: ['GlobalSearch'],
    }),

    /**
     * Get search index statistics for the workspace
     */
    getSearchIndexStats: builder.query<SearchIndexStatsResponse, void>({
      query: () => '/search/index/stats',
    }),

    /**
     * Get quick search suggestions for type-ahead
     * Optimized for speed with limited results
     */
    getSearchSuggestions: builder.query<SearchSuggestionsResponse, { q: string; types?: SearchEntityType[] }>({
      query: ({ q, types }) => {
        const params = new URLSearchParams();
        params.append('q', q);
        if (types && types.length > 0) {
          params.append('types', types.join(','));
        }
        return `/search/index/suggest?${params.toString()}`;
      },
    }),
  }),
});

export const {
  useGlobalSearchQuery,
  useLazyGlobalSearchQuery,
  useGetSearchTypesQuery,
  // Unified Search Index hooks
  useSearchIndexQuery,
  useLazySearchIndexQuery,
  useGetSearchIndexStatsQuery,
  useGetSearchSuggestionsQuery,
  useLazyGetSearchSuggestionsQuery,
} = searchApi;
